import * as turf from "@turf/turf";

/**
 * Expand a field geometry into individual Polygon features.
 *   - Polygon       → a single-element array
 *   - MultiPolygon  → one feature per sub-polygon (gaps between them preserved)
 *   - anything else / invalid / empty → []
 *
 * This is the backbone of MultiPolygon support: every place that used to assume
 * a single field Polygon now iterates over the result of this helper, so a plain
 * Polygon field degenerates to a one-element loop and behaves exactly as before.
 */
export const getSubPolygons = (
  geometry: turf.Geometry | null | undefined
): Array<turf.Feature<turf.Polygon>> => {
  if (!geometry) return [];
  try {
    if (geometry.type === "Polygon") {
      return [turf.polygon((geometry as turf.Polygon).coordinates)];
    }
    if (geometry.type === "MultiPolygon") {
      const coords = (geometry as turf.MultiPolygon).coordinates;
      if (!Array.isArray(coords)) return [];
      return coords.reduce<Array<turf.Feature<turf.Polygon>>>(
        (acc, polygonCoords) => {
          try {
            acc.push(turf.polygon(polygonCoords));
          } catch {
            // Skip a malformed sub-polygon rather than discarding the whole field
          }
          return acc;
        },
        []
      );
    }
  } catch {
    return [];
  }
  return [];
};

/**
 * Convenience wrapper that pulls the sub-polygons of the *selected* field out of
 * the map FeatureCollection (its first feature). Returns [] when missing.
 */
export const getFieldPolygons = (
  geoJson:
    | { features?: Array<{ geometry?: turf.Geometry | null }> }
    | null
    | undefined
): Array<turf.Feature<turf.Polygon>> =>
  getSubPolygons(geoJson?.features?.[0]?.geometry as turf.Geometry);

/**
 * Returns the sub-polygon with the greatest geodetic area (m²), or null when the
 * geometry has no usable polygon. Useful for picking a single part to seed a new
 * plot block into.
 */
export const getLargestSubPolygon = (
  geometry: turf.Geometry | null | undefined
): turf.Feature<turf.Polygon> | null => {
  const polygons = getSubPolygons(geometry);
  if (polygons.length === 0) return null;
  // Seed the reduce with the first polygon (guaranteed to exist by the guard
  // above) so it never runs without an initial value.
  return polygons.reduce(
    (best, current) => (turf.area(current) > turf.area(best) ? current : best),
    polygons[0]
  );
};

/**
 * Converts a MultiPolygon geometry to a single Polygon by:
 *   1. Using turf.flatten() to split it into individual Polygon features
 *   2. Picking the one with the greatest geodetic area (m²) via turf.area()
 *
 * Why not turf.union()? If sub-polygons don't physically touch, union()
 * returns a MultiPolygon right back — solving nothing.
 * Why not turf.convex()? It draws a convex hull, distorting the real boundary.
 *
 * Picking the largest sub-polygon is correct for field geometries where a
 * MultiPolygon is typically one main field body + small detached patches.
 *
 * Returns the geometry unchanged if it is already a Polygon, or null/empty.
 */
export const normalizeMultiPolygonToPolygon = (
  geometry: turf.Geometry | null | undefined,
  fieldName?: string
): turf.Polygon | turf.Geometry | null | undefined => {
  if (!geometry) return geometry;
  if (geometry.type !== "MultiPolygon") return geometry;

  const multiPolygon = geometry as turf.MultiPolygon;
  if (
    !Array.isArray(multiPolygon.coordinates) ||
    multiPolygon.coordinates.length === 0
  )
    return geometry;

  try {
    // turf.flatten splits the MultiPolygon into individual Polygon features
    const flattened = turf.flatten(turf.multiPolygon(multiPolygon.coordinates));

    const largest = flattened.features.reduce(
      (
        best: turf.Feature<turf.Polygon> | null,
        current: turf.Feature<turf.Polygon>
      ) => {
        try {
          return !best || turf.area(current) > turf.area(best) ? current : best;
        } catch {
          return best;
        }
      },
      null
    );

    if (!largest) return geometry;

    console.warn(
      `[geoUtils] Field "${
        fieldName ?? "unknown"
      }" has a MultiPolygon geometry ` +
        `with ${flattened.features.length} sub-polygon(s). ` +
        "Only the largest sub-polygon will be used for the trial flow. " +
        "Smaller/non-touching sub-polygons are discarded. " +
        "If this field genuinely has multiple separate parcels, they will not be visible."
    );

    return largest.geometry;
  } catch (error) {
    // Fires when turf.multiPolygon() rejects the coordinates entirely — e.g.
    // unclosed rings, rings with < 4 points, null entries, or NaN values.
    // These come from malformed backend data, not from valid GeoJSON.
    // Return null so callers (e.g. checkPlotBoundaries) exit early on their
    // !fieldGeometry guard rather than crashing deeper in the call stack.
    console.warn("[geoUtils] Failed to normalise MultiPolygon:", error);
    return null;
  }
};
